import { z } from 'zod'

/**
 * Validation for the auth forms.
 *
 * The password rules mirror the platform's signup handler: 12 characters
 * minimum, with upper, lower, digit and symbol. Keeping them identical means
 * the meter and the server never disagree about what "strong enough" is.
 */

export const passwordSchema = z
  .string()
  .min(12, 'At least 12 characters')
  .regex(/[a-z]/, 'Include a lowercase letter')
  .regex(/[A-Z]/, 'Include an uppercase letter')
  .regex(/[0-9]/, 'Include a number')
  .regex(/[^A-Za-z0-9]/, 'Include a symbol')

export const emailSchema = z.string().email('Enter a valid email address')

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  full_name: z.string().min(1, 'Your name is required').max(100),
  company_name: z.string().max(100).optional(),
})

export type SignupValues = z.infer<typeof signupSchema>

/**
 * `remember_me` defaults rather than validates — the form's input type keeps it
 * optional so react-hook-form's resolver generic matches, while the output type
 * (what submit receives) always has it set.
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Your password is required'),
  remember_me: z.boolean().default(false),
})

/** What the form holds — remember_me may be absent until first change. */
export type LoginValues = z.input<typeof loginSchema>
/** What a valid submit produces — remember_me is always present. */
export type LoginSubmission = z.output<typeof loginSchema>

export const forgotPasswordSchema = z.object({
  email: emailSchema,
})

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })

export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>
